import React, { useCallback } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Column } from "react-base-table";
import DragHandle from "@druva-apps/orion-icons/lib/RowdragndropGreyOutline";
import LeftFixedIcon from "@druva-apps/orion-icons/lib/FixleftcolumnGreyOutline";
import RightFixedIcon from "@druva-apps/orion-icons/lib/FixrightcolumnGreyOutline";

import Input from "../../input";
import Tooltip from "../../tooltip";
import { useTable } from "../context";
import { toggleColumnVisibility, toggleColumnFixed } from "../actions";

const SortableColumn = ({ id, data }) => {
  const { fireAction, responsive } = useTable();
  const { title, draggable, togglable, fixable, hidden, frozen } = data;
  const isLeftFixed = frozen && frozen === Column.FrozenDirection.LEFT;
  const isRightFixed = frozen && frozen === Column.FrozenDirection.RIGHT;
  const { listeners, setNodeRef, transform, transition, attributes } =
    useSortable({
      id
    });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition
  };

  const toggleVisible = useCallback(
    event => {
      fireAction(
        toggleColumnVisibility({ key: id, status: !event.target.checked })
      );
    },
    [fireAction, id]
  );

  const fixColumnToLeft = useCallback(
    e => {
      e.preventDefault();

      fireAction(
        toggleColumnFixed({ key: id, direction: Column.FrozenDirection.LEFT })
      );
    },
    [fireAction, id]
  );

  const fixColumnToRight = useCallback(
    e => {
      e.preventDefault();
      fireAction(
        toggleColumnFixed({
          key: id,
          direction: Column.FrozenDirection.RIGHT
        })
      );
    },
    [fireAction, id]
  );

  return (
    <div className="oui-column" ref={setNodeRef} style={style}>
      <div className="oui-d-flex oui-align-items-center">
        <DragHandle
          className="oui-mr-2 oui-drag-handle"
          clickable
          {...(draggable ? listeners : {})}
          {...(draggable ? attributes : {})}
          disabled={!draggable}
        />
        <Input
          type="checkbox"
          label={title}
          disabled={!togglable}
          checked={!hidden}
          onChange={toggleVisible}
          aria-label={`toggle-column-${id}`}
        />
      </div>
      {!responsive && (
        <div>
          <Tooltip text="Freeze this column to left">
            <a
              href="#fixleft"
              aria-label={`Freeze column ${title} to the left`}
              role="button"
              className={cx(
                "oui-mr-2",
                "oui-column-fixer",
                isLeftFixed && "oui-active"
              )}
              disabled={!fixable}
              onClick={fixable ? fixColumnToLeft : null}
            >
              <LeftFixedIcon inheritFontColor />
            </a>
          </Tooltip>
          <Tooltip text="Freeze this column to right">
            <a
              href="#fixright"
              aria-label={`Freeze column ${title} to the right`}
              role="button"
              className={cx("oui-column-fixer", isRightFixed && "oui-active")}
              disabled={!fixable}
              onClick={fixable ? fixColumnToRight : null}
            >
              <RightFixedIcon inheritFontColor />
            </a>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

SortableColumn.propTypes = {
  id: PropTypes.string,
  data: PropTypes.object
};

export default SortableColumn;
